import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Box, Typography, Chip, IconButton,
  MenuItem, Paper, Switch, FormControlLabel, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';

/* ===== OFP Domain Library Fields ===== */
const LIBRARY_FIELDS = [
  { id: 'co2eKg', name: 'CO2e (kg)', type: 'number', source: 'OFP' },
  { id: 'emissionFactor', name: 'Emission Factor', type: 'number', source: 'OFP' },
  { id: 'activityQuantity', name: 'Activity Quantity', type: 'number', source: 'OFP' },
  { id: 'totalEmissions', name: 'Total Emissions', type: 'number', source: 'ELM' },
  { id: 'totalEnergy', name: 'Total Energy', type: 'number', source: 'ELM' },
  { id: 'carbonIntensity', name: 'Carbon Intensity', type: 'number', source: 'ELM' },
  { id: 'observedVolume', name: 'Observed Volume', type: 'number', source: 'Corridor' },
  { id: 'standardTemp', name: 'Standard Temp', type: 'number', source: 'Corridor' },
  { id: 'observedTemp', name: 'Observed Temp', type: 'number', source: 'Corridor' },
  { id: 'densityCorrection', name: 'Density Correction', type: 'number', source: 'Corridor' },
  { id: 'primaryDataShare', name: 'Primary Data Share', type: 'percentage', source: 'PACT' },
  { id: 'confidenceScore', name: 'Confidence Score', type: 'number', source: 'dVE' },
  { id: 'dqiScore', name: 'DQI Score', type: 'number', source: 'PACT' },
  { id: 'tolerance', name: 'Tolerance', type: 'percentage', source: 'Constraints' },
  { id: 'allocationPct', name: 'Allocation %', type: 'percentage', source: 'ELM' },
  { id: 'passedChecks', name: 'Passed Checks', type: 'integer', source: 'dVE' },
  { id: 'totalChecks', name: 'Total Checks', type: 'integer', source: 'dVE' },
];

const OPERATORS = [
  { symbol: '+', label: '+' }, { symbol: '-', label: '\u2212' },
  { symbol: '*', label: '\u00D7' }, { symbol: '/', label: '\u00F7' },
  { symbol: '(', label: '(' }, { symbol: ')', label: ')' },
  { symbol: ',', label: ',' },
  { symbol: '>', label: '>' }, { symbol: '>=', label: '\u2265' },
  { symbol: '<', label: '<' }, { symbol: '<=', label: '\u2264' },
  { symbol: '=', label: '=' }, { symbol: '!=', label: '\u2260' },
];

const FUNCTIONS = [
  { name: 'IF()', value: 'IF' }, { name: 'MIN()', value: 'MIN' },
  { name: 'MAX()', value: 'MAX' }, { name: 'ROUND()', value: 'ROUND' },
  { name: 'ABS()', value: 'ABS' }, { name: 'SUM()', value: 'SUM' },
  { name: 'LOOKUP()', value: 'LOOKUP' },
];

const KEYWORDS = ['CASE', 'WHEN', 'THEN', 'ELSE', 'END'];

const EXPRESSION_LANGUAGES = ['Plain Text', 'MathJS', 'Python', 'Excel'];

let _tokenCounter = 0;
const nextTokenId = () => `tok_${++_tokenCounter}`;

const tokenColor = (type) => {
  switch (type) {
    case 'field': return { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' };
    case 'operator': return { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' };
    case 'number': return { bg: '#fff3e0', color: '#e65100', border: '#ffb74d' };
    case 'function': return { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' };
    case 'keyword': return { bg: '#fce4ec', color: '#c62828', border: '#ef9a9a' };
    default: return { bg: '#f5f5f5', color: '#616161', border: '#bdbdbd' };
  }
};

const tokensToExpression = (tokens) =>
  tokens.map(t => {
    if (t.type === 'field') return t.display;
    if (t.type === 'function') return t.value + '(';
    return t.value;
  }).join(' ');

export default function FormulaBuilder({ open, onClose, onSave, formula }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formulaType, setFormulaType] = useState('');
  const [expressionLanguage, setExpressionLanguage] = useState('Plain Text');
  const [tokens, setTokens] = useState([]);
  const [constantVal, setConstantVal] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualExpr, setManualExpr] = useState('');
  const [testValues, setTestValues] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [formulaTypes, setFormulaTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldVariable, setNewFieldVariable] = useState('');
  const [newFieldType, setNewFieldType] = useState('Number');

  // Load formula types
  useEffect(() => {
    if (open) {
      import('../services/calculationService').then(mod => {
        mod.default.getTypes().then(data => setFormulaTypes(data ?? [])).catch(() => {});
      });
    }
  }, [open]);

  // Populate from existing formula
  useEffect(() => {
    if (open && formula) {
      setName(formula.name || '');
      setDescription(formula.description || '');
      setFormulaType(formula.formula_type || '');
      setExpressionLanguage(formula.expression_language || 'Plain Text');
      setManualExpr(formula.formula_expression || '');
      setManualMode(true);
      setTokens([]);
      setTestValues({});
      setTestResult(null);
    } else if (open) {
      setName('');
      setDescription('');
      setFormulaType('');
      setExpressionLanguage('Plain Text');
      setTokens([]);
      setManualExpr('');
      setManualMode(false);
      setTestValues({});
      setTestResult(null);
    }
  }, [open, formula]);

  const expression = manualMode ? manualExpr : tokensToExpression(tokens);

  const allFields = useMemo(() => [...customFields, ...LIBRARY_FIELDS], [customFields]);

  const fieldsInFormula = useMemo(() => {
    const used = new Set();
    tokens.forEach(t => { if (t.type === 'field') used.add(t.value); });
    return [...used].map(id => allFields.find(f => f.id === id)).filter(Boolean);
  }, [tokens, allFields]);

  const addToken = (token) => setTokens(prev => [...prev, { ...token, id: nextTokenId() }]);
  const removeToken = (id) => setTokens(prev => prev.filter(t => t.id !== id));

  const addField = (field) => addToken({ type: 'field', value: field.id, display: field.name });
  const addOperator = (op) => addToken({ type: 'operator', value: op.symbol, display: op.label });
  const addFunction = (fn) => addToken({ type: 'function', value: fn.value, display: fn.name });
  const addKeyword = (kw) => addToken({ type: 'keyword', value: kw, display: kw });

  const addConstant = () => {
    const v = constantVal.trim();
    if (v && !isNaN(Number(v))) {
      addToken({ type: 'number', value: v, display: v });
      setConstantVal('');
    }
  };

  const handleAddField = () => {
    const label = newFieldLabel.trim();
    const variable = newFieldVariable.trim() || label.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (!label) return;
    setCustomFields(prev => [...prev, { id: variable, name: label, type: newFieldType.toLowerCase(), source: 'Calculation' }]);
    setNewFieldLabel('');
    setNewFieldVariable('');
    setNewFieldType('Number');
    setShowAddField(false);
  };

  const removeCustomField = (id) => {
    setCustomFields(prev => prev.filter(f => f.id !== id));
    setTokens(prev => prev.filter(t => !(t.type === 'field' && t.value === id)));
  };

  const handleSave = () => {
    onSave({
      name,
      description,
      formula_expression: expression,
      expression_language: expressionLanguage,
      formula_type: formulaType,
      components: tokens.map((t, idx) => ({
        emission_calculation_component_type: t.type === 'field' ? 'Variable' : t.type === 'operator' ? 'Operator' : t.type === 'function' ? 'Function' : t.type === 'keyword' ? 'Keyword' : 'Constant',
        emission_calculation_component_order: idx + 1,
        emission_calculation_component_id: t.value,
        numeric_constant: t.type === 'number' ? parseFloat(t.value) : null
      }))
    });
  };

  const runTest = () => {
    try {
      let expr = expression;
      fieldsInFormula.forEach(f => {
        const val = testValues[f.id];
        if (val === undefined || val === '') throw new Error(`Missing value for "${f.name}"`);
        const regex = new RegExp(f.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        expr = expr.replace(regex, val);
      });
      expr = expr.replace(/\bABS\b/g, 'Math.abs').replace(/\bMIN\b/g, 'Math.min')
        .replace(/\bMAX\b/g, 'Math.max').replace(/\bROUND\b/g, 'Math.round');
      expr = expr.replace(/\u00D7/g, '*').replace(/\u00F7/g, '/').replace(/\u2212/g, '-');
      // eslint-disable-next-line no-new-func
      const result = new Function(`"use strict"; return (${expr})`)();
      setTestResult({ ok: true, value: result });
    } catch (err) {
      setTestResult({ ok: false, value: err.message });
    }
  };

  const canSave = name.trim() && expression.trim();

  /* Sidebar: all fields in this calculation (custom + used from library) */
  const sidebarFields = useMemo(() => {
    const libraryUsed = fieldsInFormula.filter(f => !customFields.find(cf => cf.id === f.id));
    return [...customFields, ...libraryUsed];
  }, [customFields, fieldsInFormula]);

  const typeIcon = (type) => {
    if (type === 'percentage') return '%';
    if (type === 'integer') return '#';
    return 'r';
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {formula ? `Edit: ${formula.name}` : 'New Calculation Formula'}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', minHeight: 500 }}>
          {/* Main builder area */}
          <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
            {/* Metadata */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField fullWidth label="Name" required value={name} onChange={e => setName(e.target.value)} size="small" />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth select label="Formula Type" value={formulaType} onChange={e => setFormulaType(e.target.value)} size="small">
                  <MenuItem value="">None</MenuItem>
                  {formulaTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth select label="Expression Language" value={expressionLanguage} onChange={e => setExpressionLanguage(e.target.value)} size="small">
                  {EXPRESSION_LANGUAGES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={2} value={description} onChange={e => setDescription(e.target.value)} size="small" />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Toggle: Visual / Manual */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Formula Builder</Typography>
              <FormControlLabel
                control={<Switch checked={manualMode} onChange={e => setManualMode(e.target.checked)} size="small" />}
                label={<Typography variant="caption">Manual Edit</Typography>}
              />
            </Box>

            {manualMode ? (
              <TextField
                fullWidth multiline rows={3} value={manualExpr}
                onChange={e => setManualExpr(e.target.value)}
                placeholder="Type your formula expression..."
                sx={{ mb: 2, fontFamily: 'monospace' }}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            ) : (
              <>
                {/* Available Fields (custom) */}
                {customFields.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Available Fields
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 2 }}>
                      {customFields.map(f => (
                        <Chip key={f.id} label={f.name} size="small" color="success" variant="outlined"
                          onClick={() => addField(f)}
                          sx={{ cursor: 'pointer', fontSize: '0.75rem' }} />
                      ))}
                    </Box>
                  </>
                )}

                {/* Library Fields */}
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Library Fields
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 2 }}>
                  {LIBRARY_FIELDS.map(f => (
                    <Chip key={f.id} label={f.name} size="small" variant="outlined" color="primary"
                      onClick={() => addField(f)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }} />
                  ))}
                </Box>

                {/* Operators & Functions */}
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Operators & Functions
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1 }}>
                  {OPERATORS.map(op => (
                    <Button key={op.symbol} variant="outlined" size="small" onClick={() => addOperator(op)}
                      sx={{ minWidth: 36, px: 1, fontSize: '0.85rem' }}>
                      {op.label}
                    </Button>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField size="small" placeholder="Constant..." value={constantVal}
                      onChange={e => setConstantVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addConstant(); }}
                      sx={{ width: 100 }}
                      InputProps={{ sx: { height: 30, fontSize: '0.8rem' } }} />
                    <IconButton size="small" color="primary" onClick={addConstant}><AddIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {FUNCTIONS.map(fn => (
                    <Button key={fn.value} variant="outlined" size="small" color="success" onClick={() => addFunction(fn)}
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
                      {fn.name}
                    </Button>
                  ))}
                  {KEYWORDS.map(kw => (
                    <Button key={kw} variant="outlined" size="small" color="error" onClick={() => addKeyword(kw)}
                      sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {kw}
                    </Button>
                  ))}
                </Box>

                {/* Formula Canvas */}
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Formula
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, minHeight: 50, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5, mb: 2, bgcolor: 'grey.50' }}>
                  {tokens.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      Click fields and operators above to build your formula...
                    </Typography>
                  ) : tokens.map(token => {
                    const colors = tokenColor(token.type);
                    return (
                      <Chip
                        key={token.id}
                        icon={<DragIndicatorIcon sx={{ fontSize: 14 }} />}
                        label={token.display}
                        size="small"
                        onDelete={() => removeToken(token.id)}
                        sx={{
                          bgcolor: colors.bg, color: colors.color,
                          borderColor: colors.border, border: '1px solid',
                          fontFamily: token.type === 'number' ? 'monospace' : 'inherit',
                          '& .MuiChip-icon': { color: colors.color, opacity: 0.5 }
                        }}
                      />
                    );
                  })}
                </Paper>
              </>
            )}

            {/* Preview */}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
              Preview
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {expression || <em style={{ color: '#999' }}>Empty formula</em>}
              </Typography>
            </Paper>

            {/* Test Section */}
            {!manualMode && fieldsInFormula.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <PlayArrowIcon color="success" fontSize="small" /> Test Formula
                </Typography>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  {fieldsInFormula.map(f => (
                    <Grid item xs={3} key={f.id}>
                      <TextField fullWidth size="small" type="number" label={f.name}
                        value={testValues[f.id] || ''}
                        onChange={e => setTestValues(prev => ({ ...prev, [f.id]: e.target.value }))} />
                    </Grid>
                  ))}
                </Grid>
                <Button variant="contained" color="success" size="small" startIcon={<PlayArrowIcon />} onClick={runTest} sx={{ mb: 1 }}>
                  Run
                </Button>
                {testResult && (
                  <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: testResult.ok ? '#e8f5e9' : '#ffebee' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: testResult.ok ? '#2e7d32' : '#c62828' }}>
                      {testResult.ok ? `Result: ${typeof testResult.value === 'number' ? testResult.value.toFixed(6) : testResult.value}` : `Error: ${testResult.value}`}
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </Box>

          {/* Sidebar: This Calculation */}
          <Box sx={{ width: 280, borderLeft: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', p: 2, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                This Calculation
              </Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddField(true)}
                sx={{ fontSize: '0.7rem', textTransform: 'none', px: 1.5, py: 0.5 }}>
                Add
              </Button>
            </Box>

            {sidebarFields.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                No fields defined yet. Click "Add" to create custom fields, or add library fields to the formula.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {sidebarFields.map(f => {
                  const isCustom = customFields.some(cf => cf.id === f.id);
                  return (
                    <Paper key={f.id} variant="outlined" sx={{ p: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                      onClick={() => { if (!manualMode) addField(f); }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{f.name}</Typography>
                        {isCustom && (
                          <IconButton size="small" onClick={e => { e.stopPropagation(); removeCustomField(f.id); }}
                            sx={{ p: 0.25 }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {typeIcon(f.type)} &middot; {f.type}
                        </Typography>
                        <Chip label={f.source || 'Calculation'} size="small" variant="outlined" color="info"
                          sx={{ fontSize: '0.65rem', height: 18, ml: 0.5 }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Field Sources Legend */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Field Sources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                {[
                  { src: 'OFP', desc: 'Open Footprint' },
                  { src: 'ELM', desc: 'E-Ledger Mgmt' },
                  { src: 'Corridor', desc: 'Energy Corridor' },
                  { src: 'PACT', desc: 'WBCSD PACT' },
                  { src: 'dVE', desc: 'Verification Engine' },
                  { src: 'Calculation', desc: 'Custom Field' },
                ].map(({ src, desc }) => (
                  <Box key={src} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip label={src} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{desc}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>

    {/* Add Field Dialog */}
    <Dialog open={showAddField} onClose={() => setShowAddField(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Add Field</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth label="Label" placeholder="e.g. Base Salary" size="small"
          value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
          autoFocus
        />
        <TextField
          fullWidth label="Variable Name" placeholder="e.g. base_salary" size="small"
          value={newFieldVariable} onChange={e => setNewFieldVariable(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Auto-generated from label if left empty"
        />
        <TextField
          fullWidth select label="Type" size="small"
          value={newFieldType} onChange={e => setNewFieldType(e.target.value)}
        >
          <MenuItem value="Number">Number</MenuItem>
          <MenuItem value="Percentage">Percentage</MenuItem>
          <MenuItem value="Integer">Integer</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowAddField(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleAddField} disabled={!newFieldLabel.trim()}>Add Field</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
